import { differenceInYears, differenceInMonths, differenceInCalendarDays, differenceInHours, differenceInMinutes, formatDistanceStrict } from "date-fns"

import i18n from "@/plugins/i18n"

// Returns formatted time like "00:22"
export const secondsToTimeString = function (seconds) {
  if (seconds == null) {
    return null
  }

  const hrs = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)

  const pad = (n) => n.toString().padStart(2, "0")

  if (hrs === 0) {
    return `${pad(mins)}:${pad(secs)}`
  }
  return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`
}

export const timeAgo = function (date, { shortFormat } = {}) {
  return shortFormat ? timeAgoShort(date) : timeAgoFull(date)
}

export const timeAgoFull = function (date) {
  const values = getValues(date)

  if (values.years > 0) {
    return i18n.global.tc("dates.year", values.years)
  }

  if (values.months > 0) {
    return i18n.global.tc("dates.month", values.months)
  }

  if (values.days > 0) {
    return i18n.global.tc("dates.day", values.days)
  }

  if (values.hours > 0) {
    return i18n.global.tc("dates.hourLong", values.hours)
  }

  if (values.minutes > 0) {
    return i18n.global.tc("dates.minute", values.minutes)
  }

  return i18n.global.t("dates.now")
}

export const timeAgoShort = function (date) {
  const values = getValues(date)

  if (values.years > 0) {
    return i18n.global.t("dates.yearShort", { count: values.years })
  }

  if (values.months > 0) {
    return i18n.global.t("dates.monthShort", { count: values.months })
  }

  if (values.days > 0) {
    return i18n.global.t("dates.dayShort", { count: values.days })
  }

  if (values.hours > 0) {
    return i18n.global.tc("dates.hourShort", values.hours, { count: values.hours })
  }

  if (values.minutes > 0) {
    return i18n.global.tc("dates.minuteShort", values.minutes, { count: values.minutes })
  }

  return i18n.global.t("dates.now")
}

export const timeCount = function (type, number) {
  if (!["minute", "hour", "day", "month", "year"].includes(type)) {
    console.error(`Dates timeCount, unsupported type: ${type}`)
    return ""
  }
  return i18n.global.tc(`dates.${type}Count`, number)
}

export const getFormattedShortTime = (date) => {
  const values = getValues(date)
  const messageDate = new Date(date)

  if (values.years > 0) {
    return messageDate.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
  }

  if (values.days > 0) {
    return messageDate.toLocaleDateString(undefined, { day: "numeric", month: "short" })
  }

  return timeAgoShort(date)
}

export const getFormattedDate = (date, options = {}) => {
  const dateToFormat = new Date(date)
  const optionsComputed = { weekday: "short", day: "numeric", month: "short", year: "numeric", ...options }

  return dateToFormat.toLocaleDateString(undefined, optionsComputed).replace(/,/g, "")
}

export const getFormattedTime = (date, { timeZone } = {}) => {
  const dateToFormat = new Date(date)
  return dateToFormat.toLocaleTimeString(undefined, { hour: "numeric", minute: "numeric", timeZone })
}

export const getDateParts = (date, { timeZone } = {}) => {
  const dateToFormat = new Date(date)
  return {
    day: dateToFormat.toLocaleDateString(undefined, { day: "numeric", timeZone }),
    dayName: dateToFormat.toLocaleDateString(undefined, { weekday: "long", timeZone }),
    monthName: dateToFormat.toLocaleDateString(undefined, { month: "short", timeZone }),
    monthLongName: dateToFormat.toLocaleDateString(undefined, { month: "long", timeZone })
  }
}

export const getLocalTimezone = (date) => {
  const abbreviation = new Intl.DateTimeFormat(window.navigator.language, { timeZoneName: "short" })
    .formatToParts(new Date(date))
    .find((part) => part.type === "timeZoneName").value

  return abbreviation.match(/-|\+/) ? Intl.DateTimeFormat().resolvedOptions().timeZone : abbreviation
}

export const getFormattedDateWithTime = (date, { timeZone } = {}) => {
  const dateToFormat = new Date(date)
  return dateToFormat.toLocaleTimeString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    timeZone
  })
}

export const getValues = function (date) {
  const now = new Date()
  const dateToCompare = new Date(date)

  return {
    years: differenceInYears(now, dateToCompare),
    months: differenceInMonths(now, dateToCompare),
    days: differenceInCalendarDays(now, dateToCompare),
    hours: differenceInHours(now, dateToCompare),
    minutes: differenceInMinutes(now, dateToCompare)
  }
}

export const uses12HourSystem = () => {
  const testDate = new Date("2000-01-01")
  testDate.setHours(13)
  return getFormattedTime(testDate).includes("PM")
}

//https://github.com/date-fns/date-fns/issues/1815#issuecomment-762588179
export const getDistance = (date1, date2) => {
  const [hours, minutes, seconds] = ["hour", "minute", "second"].map((unit) =>
    formatDistanceStrict(date1, date2, {
      unit,
      roundingMethod: "floor"
    }).replace(/\D/g, "")
  )

  return [hours % 24, minutes % 60, seconds % 60].map((s) => `${s}`.padStart(2, "0")).join(":")
}
